// What the interview's user turns say, without the wrapping the server
// put on them for the model: the fence around anything the client wrote,
// the "Q: / A:" frame around an answer, the document header. Assistant
// turns are the model's JSON and never reach the screen.

import type { InterviewTurn } from "../api/intake";

export interface TranscriptEntry {
  kind: "story" | "answer" | "document";
  /** The question answered; absent for a story, a document, or a turn
      sent without a live question (the server labels those "Question N"). */
  question?: string;
  /** The document's file label. */
  label?: string;
  text: string;
}

function unfence(s: string): string {
  return s.replace(/^<document>\n?/, "").replace(/\n?<\/document>$/, "");
}

// The server's sentinel for a document that repeated what the interview
// already knew; the chip alone says enough.
const NOTHING_NEW = "Nothing new was read from it.";

export function parseUserTurn(content: string): TranscriptEntry {
  const doc = /^\[Uploaded document: (.+?)\]\n([\s\S]*)$/.exec(content);
  if (doc) {
    const rest = doc[2];
    return {
      kind: "document",
      label: doc[1],
      text: rest === NOTHING_NEW ? "" : unfence(rest.replace(/^Facts read from it:\n/, "")),
    };
  }
  const answer = /^Q: ([\s\S]*?)\nA: ([\s\S]*)$/.exec(content);
  if (answer) {
    const question = /^Question \d+$/.test(answer[1]) ? undefined : answer[1];
    return { kind: "answer", question, text: unfence(answer[2]) };
  }
  return { kind: "story", text: unfence(content) };
}

export function transcriptOf(history: InterviewTurn[]): TranscriptEntry[] {
  return history.filter((t) => t.role === "user").map((t) => parseUserTurn(t.content));
}

const FIELD_LABELS: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  dateOfBirth: "Date of birth",
  alienNumber: "A-Number",
  countryOfBirth: "Country of birth",
  dateOfEntry: "Date of entry",
  mannerOfEntry: "Manner of entry",
  email: "Email",
  phone: "Phone",
  preferredLanguage: "Language",
};

/** The label a session field wears on screen - the same in the side
    panel and in a document turn's "Read from it" line. */
export function labelOf(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key.replace(/([A-Z])/g, (c) => ` ${c.toLowerCase()}`).replace(/^./, (c) => c.toUpperCase())
  );
}

/** The "key: value" lines a document turn carries, relabelled and joined. */
export function factLabels(text: string): string {
  return text
    .split("\n")
    .map((line) => {
      const at = line.indexOf(": ");
      return at > 0 ? `${labelOf(line.slice(0, at))}: ${line.slice(at + 2)}` : line;
    })
    .join("; ");
}
